/**
 * The spark theme's colour scheme.
 *
 * `spark.css` keys its dark ramp off a `dark` class, and expects that class to be explicit: a
 * `system` preference is resolved here against `prefers-color-scheme` rather than in a media query
 * over the tokens. That way the tokens and Tailwind's own `dark:` variant always agree on one
 * selector, instead of the media query flipping the tokens while the variant stays off.
 */

/** What the user asked for. `system` follows the OS. */
export type ColorSchemePreference = 'light' | 'dark' | 'system';

/** What `system` resolved to, and what the `dark`/`light` class on `<html>` says. */
export type ResolvedColorScheme = 'light' | 'dark';

export const COLOR_SCHEME_STORAGE_KEY = 'colorScheme';

const PREFERENCES: readonly ColorSchemePreference[] = ['light', 'dark', 'system'];

/** Where a user with no stored choice starts. `system` is opt-in, never a default. */
export const DEFAULT_COLOR_SCHEME_PREFERENCE: ColorSchemePreference = 'light';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isPreference(value: unknown): value is ColorSchemePreference {
  return PREFERENCES.includes(value as ColorSchemePreference);
}

/** The OS preference, or `light` where `matchMedia` isn't available. */
export function systemColorScheme(): ResolvedColorScheme {
  return typeof window !== 'undefined' && window.matchMedia?.(DARK_QUERY).matches
    ? 'dark'
    : 'light';
}

export function resolveColorScheme(preference: ColorSchemePreference): ResolvedColorScheme {
  return preference === 'system' ? systemColorScheme() : preference;
}

/** Falls back to `light` for a missing, malformed or unreadable value. */
export function readColorSchemePreference(): ColorSchemePreference {
  try {
    const stored = window.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
    return isPreference(stored) ? stored : DEFAULT_COLOR_SCHEME_PREFERENCE;
  } catch {
    // Private mode, or site data blocked. The preference is a convenience, not state we need.
    return DEFAULT_COLOR_SCHEME_PREFERENCE;
  }
}

export function storeColorSchemePreference(preference: ColorSchemePreference): void {
  try {
    window.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
  } catch {
    // As above — losing the write costs the user a re-pick, nothing more.
  }
}

/**
 * Writes the resolved scheme onto `<html>` and returns it. Both classes are managed, never just
 * `dark` added, so a stale `light` from a previous preference can't sit alongside it.
 */
export function applyColorScheme(preference: ColorSchemePreference): ResolvedColorScheme {
  const resolved = resolveColorScheme(preference);
  const { classList } = document.documentElement;

  classList.toggle('dark', resolved === 'dark');
  classList.toggle('light', resolved === 'light');

  return resolved;
}

/**
 * Applies the stored preference and keeps it in step with the OS.
 */
export function initColorScheme(): () => void {
  const preference = readColorSchemePreference();
  applyColorScheme(preference);

  const media = window.matchMedia?.(DARK_QUERY);
  if (!media) {
    return () => {};
  }

  // Only `system` tracks the OS; an explicit choice outranks it.
  const onChange = () => {
    if (readColorSchemePreference() === 'system') {
      applyColorScheme('system');
    }
  };

  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
